import { IconCertificate, IconDownload } from "@tabler/icons-react"
import { type FC } from "react"
import { useParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/core/utilities/number"

import Button from "~/components/Button"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { downloadCsv } from "~/utilities/csv"
import routes from "~/utilities/routes"
import { componentBackgroundColor, componentBorderColor, dangerColor, successColor } from "~/utilities/styles"
import { query } from "~/utilities/trpc"

import type { EnrolledStudents } from "../../../../api/src/course/models"

type EnrolledStudent = EnrolledStudents["users"][number]

function getUnlockedReasonLabel(reason: EnrolledStudent["unlockedReason"]): string {
	switch (reason) {
		case "free":
			return "Free"
		case "purchased":
			return "Purchased"
		case "membership":
			return "Membership"
		case "expired-membership":
			return "Expired membership"
		case "bundle":
			return "Bundle"
		default:
			return "Unknown"
	}
}

function downloadEnrolledStudentsCsv(courseTitle: string, users: EnrolledStudent[]) {
	downloadCsv(
		`${courseTitle} - Enrolled students.csv`,
		[
			{ title: "First name", value: user => user.firstName },
			{ title: "Last name", value: user => user.lastName },
			{ title: "Email", value: user => user.email },
			{ title: "Access", value: user => getUnlockedReasonLabel(user.unlockedReason) },
			{ title: "Progress percent", value: user => user.progressPercent?.toString() ?? "" },
			{ title: "Certificate date", value: user => user.certificate?.createDate.toISOString() ?? "" },
		],
		users,
	)
}

const EnrolledStudentsPage: FC = () => {
	const courseId = parseNumber(useParams().courseId)

	const { data, error } = query.course.listEnrolledStudents.useQuery(courseId ?? -1)

	const users = data ? [...data.users].sort((a, b) => (b.progressPercent ?? 0) - (a.progressPercent ?? 0)) : []

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title="Enrolled students"
					path={[
						{ title: "Courses", link: routes.course.index() },
						{
							title: data?.course.title ?? "...",
							link: data ? routes.course.detail(data.course.id) : undefined,
						},
					]}
				/>
				{data && (
					<Button
						variant="secondary"
						className="py-2"
						isDisabled={users.length === 0}
						onClick={() => downloadEnrolledStudentsCsv(data.course.title, users)}
					>
						<IconDownload className="size-4" aria-hidden="true" /> Export CSV
					</Button>
				)}
			</PageHeader>
			{error ? (
				<p>Failed to load data.</p>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : (
				<Container>
					{users.map(user => (
						<User key={user.id}>
							<Email>{user.email}</Email>
							<Name>
								{user.firstName} {user.lastName}
							</Name>
							<Tags>
								{user.certificate && (
									<Link
										to={routes.user.certificate(user.id, user.certificate.id)}
										className="border-primary/20 bg-primary/10 text-primary flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-1.5 text-xs leading-none font-semibold"
									>
										<IconCertificate className="size-4" />
										Certificate
									</Link>
								)}
								{user.unlockedReason !== "free" && (
									<Tag $color={successColor}>{getUnlockedReasonLabel(user.unlockedReason)}</Tag>
								)}
								<Tag $color={dangerColor}>
									{user.progressPercent !== null ? `${user.progressPercent}%` : "Not started"}
								</Tag>
							</Tags>
						</User>
					))}
				</Container>
			)}
		</Layout>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const User = styled.div`
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 12px;
	padding: 12px 16px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16px;
`

const Email = styled.div`
	flex: 0 0 300px;
	font-weight: 600;
	font-size: 14px;
`

const Name = styled.p`
	flex: 1 0 0;
	font-weight: 600;
	font-size: 14px;
`

const Tags = styled.div`
	display: flex;
	gap: 16px;
`

const Tag = styled.div<{ $color: string }>`
	font-weight: 600;
	font-size: 12px;
	line-height: 1;
	color: ${props => props.$color};
	background-color: ${props => props.$color}10;
	border-radius: 12px;
	padding: 6px 12px;
`

export default EnrolledStudentsPage
